import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Modal, Button, Row, Col } from 'react-bootstrap';
import ConfirmModal from 'components/ConfirmModal';
import {
    getPolicies, createPolicy, updatePolicy, deletePolicy,
    getBalances, createBalance, adjustBalance, deleteBalance,
    getRequests, createRequest, approveRequest, denyRequest, deleteRequest,
    getHolidays, createHoliday, updateHoliday, deleteHoliday,
    createNotification,
} from 'services/timeOff';
import { getEmployees } from 'services/employees';
import ENV from 'constants/env';

const TABS = ['Requests', 'Balances', 'Policies', 'Holidays'];

// Calculate US federal holidays dynamically for any year
const getNthWeekday = (year, month, weekday, nth) => {
    // nth: 1=first, 2=second, -1=last
    if (nth === -1) {
        // Last weekday of month
        const lastDay = new Date(year, month + 1, 0);
        const diff = (lastDay.getDay() - weekday + 7) % 7;
        return new Date(year, month, lastDay.getDate() - diff);
    }
    const first = new Date(year, month, 1);
    const diff = (weekday - first.getDay() + 7) % 7;
    return new Date(year, month, 1 + diff + (nth - 1) * 7);
};

const toDateStr = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
};

const getUSFederalHolidays = (year) => [
    { name: "New Year's Day",            date: toDateStr(new Date(year, 0, 1)),   hours: 8 },
    { name: 'Martin Luther King Jr. Day', date: toDateStr(getNthWeekday(year, 0, 1, 3)), hours: 8 }, // 3rd Mon Jan
    { name: "Presidents' Day",           date: toDateStr(getNthWeekday(year, 1, 1, 3)), hours: 8 }, // 3rd Mon Feb
    { name: 'Memorial Day',              date: toDateStr(getNthWeekday(year, 4, 1, -1)), hours: 8 }, // Last Mon May
    { name: 'Juneteenth',               date: toDateStr(new Date(year, 5, 19)),   hours: 8 },
    { name: 'Independence Day',          date: toDateStr(new Date(year, 6, 4)),   hours: 8 },
    { name: 'Labor Day',                 date: toDateStr(getNthWeekday(year, 8, 1, 1)),  hours: 8 }, // 1st Mon Sep
    { name: 'Columbus Day',              date: toDateStr(getNthWeekday(year, 9, 1, 2)),  hours: 8 }, // 2nd Mon Oct
    { name: 'Veterans Day',              date: toDateStr(new Date(year, 10, 11)), hours: 8 },
    { name: 'Thanksgiving Day',          date: toDateStr(getNthWeekday(year, 10, 4, 4)), hours: 8 }, // 4th Thu Nov
    { name: 'Christmas Day',             date: toDateStr(new Date(year, 11, 25)), hours: 8 },
];

const TIME_OFF_TYPES = ['Vacation', 'Sick', 'Holiday', 'Personal', 'PTO'];
const ACCRUAL_METHODS = ['manual', 'per_pay_period', 'per_hour_worked', 'annual_lump_sum'];

const STATUS_COLORS = {
    pending:  { bg: '#fff3cd', color: '#856404' },
    approved: { bg: '#d1e7dd', color: '#0a3622' },
    denied:   { bg: '#f8d7da', color: '#842029' },
    paid:     { bg: '#e9ecef', color: '#000000' },
};

const fmtDate = (d) => {
    if (!d) return '—';
    const s = typeof d === 'string' ? d : d.toISOString();
    return new Date(s).toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric', timeZone: 'UTC' });
};

const unwrap = (r) => Array.isArray(r) ? r : (r?.data ?? r?.items ?? []);

function PolicyModal({ policy, onClose, onSave, isPending, errorMsg }) {
    const [form, setForm] = useState({
        name: policy?.name ?? '',
        timeOffType: policy?.timeOffType ?? 'Vacation',
        accrualMethod: policy?.accrualMethod ?? 'manual',
        accrualRatePerPeriod: policy?.accrualRatePerPeriod ?? '',
        maxAccrualHours: policy?.maxAccrualHours ?? '',
        maxCarryoverHours: policy?.maxCarryoverHours ?? '',
        isActive: policy?.isActive ?? true,
        allowNegativeBalance: policy?.allowNegativeBalance ?? true,
    });

    const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

    return (
        <Modal show onHide={onClose} centered>
            <Modal.Header closeButton style={{ background: '#1B75F0', color: '#fff' }}>
                <Modal.Title style={{ fontSize: 16 }}>{policy ? 'Edit Policy' : 'Add Time Off Policy'}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Row className="g-3">
                    <Col md={12}>
                        <label className="form-label" style={{ fontSize: 13 }}>Policy Name *</label>
                        <input className="form-control form-control-sm" value={form.name} onChange={e => set('name', e.target.value)} />
                    </Col>
                    <Col md={6}>
                        <label className="form-label" style={{ fontSize: 13 }}>Type *</label>
                        <select className="form-select form-select-sm" value={form.timeOffType} onChange={e => set('timeOffType', e.target.value)}>
                            {TIME_OFF_TYPES.map(t => <option key={t}>{t}</option>)}
                        </select>
                    </Col>
                    <Col md={6}>
                        <label className="form-label" style={{ fontSize: 13 }}>Accrual Method</label>
                        <select className="form-select form-select-sm" value={form.accrualMethod} onChange={e => set('accrualMethod', e.target.value)}>
                            {ACCRUAL_METHODS.map(m => <option key={m} value={m}>{m.replace(/_/g, ' ')}</option>)}
                        </select>
                    </Col>
                    {form.accrualMethod !== 'manual' && (
                        <Col md={4}>
                            <label className="form-label" style={{ fontSize: 13 }}>Hrs / Period</label>
                            <input type="number" className="form-control form-control-sm" value={form.accrualRatePerPeriod} onChange={e => set('accrualRatePerPeriod', e.target.value)} placeholder="e.g. 3.08" />
                        </Col>
                    )}
                    <Col md={4}>
                        <label className="form-label" style={{ fontSize: 13 }}>Max Accrual Hrs</label>
                        <input type="number" className="form-control form-control-sm" value={form.maxAccrualHours} onChange={e => set('maxAccrualHours', e.target.value)} placeholder="e.g. 120" />
                    </Col>
                    <Col md={4}>
                        <label className="form-label" style={{ fontSize: 13 }}>Max Carryover Hrs</label>
                        <input type="number" className="form-control form-control-sm" value={form.maxCarryoverHours} onChange={e => set('maxCarryoverHours', e.target.value)} placeholder="e.g. 40" />
                    </Col>
                    <Col md={12}>
                        <div style={{ background: '#f8f9fa', borderRadius: 8, padding: '12px 14px', border: '1px solid #e9ecef' }}>
                            <div style={{ fontSize: 13, fontWeight: 600, color: '#333', marginBottom: 10 }}>Balance Rules</div>
                            <div className="d-flex flex-column gap-2">
                                <div className="d-flex align-items-start gap-3">
                                    <div style={{ paddingTop: 2 }}>
                                        <div
                                            onClick={() => set('allowNegativeBalance', !form.allowNegativeBalance)}
                                            style={{
                                                width: 40, height: 22, borderRadius: 11, cursor: 'pointer', transition: 'background 0.2s',
                                                background: form.allowNegativeBalance ? '#1B75F0' : '#ccc',
                                                position: 'relative', flexShrink: 0,
                                            }}
                                        >
                                            <div style={{
                                                position: 'absolute', top: 3, left: form.allowNegativeBalance ? 21 : 3,
                                                width: 16, height: 16, borderRadius: '50%', background: '#fff',
                                                transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                                            }} />
                                        </div>
                                    </div>
                                    <div>
                                        <div style={{ fontSize: 13, fontWeight: 500, color: '#333' }}>
                                            Allow Negative Balance
                                        </div>
                                        <div style={{ fontSize: 11, color: '#888', marginTop: 1 }}>
                                            {form.allowNegativeBalance
                                                ? 'Employees can request time off even when balance reaches zero. Balance may go negative.'
                                                : 'Employees cannot request time off once their balance reaches zero. Excess hours treated as unpaid in payroll.'}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Col>
                    <Col md={12}>
                        <div className="form-check">
                            <input type="checkbox" className="form-check-input" id="isActive" checked={form.isActive} onChange={e => set('isActive', e.target.checked)} />
                            <label className="form-check-label" htmlFor="isActive" style={{ fontSize: 13 }}>Active</label>
                        </div>
                    </Col>
                    {errorMsg && (
                        <Col md={12}>
                            <div className="alert alert-danger py-2 mb-0" style={{ fontSize: 12 }}>{errorMsg}</div>
                        </Col>
                    )}
                </Row>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" size="sm" onClick={onClose} disabled={isPending}>Cancel</Button>
                <Button size="sm" style={{ backgroundColor: '#1B75F0', border: 'none' }}
                    onClick={() => onSave({
                        ...form,
                        accrualRatePerPeriod: form.accrualRatePerPeriod !== '' ? Number(form.accrualRatePerPeriod) : null,
                        maxAccrualHours: form.maxAccrualHours !== '' ? Number(form.maxAccrualHours) : null,
                        maxCarryoverHours: form.maxCarryoverHours !== '' ? Number(form.maxCarryoverHours) : null,
                    })}
                    disabled={!form.name || isPending}
                >
                    {isPending ? 'Saving…' : 'Save'}
                </Button>
            </Modal.Footer>
        </Modal>
    );
}

function AssignBalanceModal({ policies, employees, onClose, onSave, isPending }) {
    const [policyId, setPolicyId] = useState('');
    const [balanceHours, setBalanceHours] = useState('');
    const [search, setSearch] = useState('');
    const [selectedIds, setSelectedIds] = useState(new Set());

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        return q ? employees.filter(e => `${e.firstName} ${e.lastName}`.toLowerCase().includes(q)) : employees;
    }, [employees, search]);

    const allSelected = filtered.length > 0 && filtered.every(e => selectedIds.has(e.id));

    const toggleAll = () => {
        if (allSelected) {
            setSelectedIds(prev => { const s = new Set(prev); filtered.forEach(e => s.delete(e.id)); return s; });
        } else {
            setSelectedIds(prev => { const s = new Set(prev); filtered.forEach(e => s.add(e.id)); return s; });
        }
    };

    const toggleOne = (id) => {
        setSelectedIds(prev => { const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s; });
    };

    const canSubmit = selectedIds.size > 0 && policyId && !isPending;

    return (
        <Modal show onHide={onClose} centered size="lg">
            <Modal.Header closeButton style={{ background: '#1B75F0', color: '#fff' }}>
                <Modal.Title style={{ fontSize: 16 }}>Assign Time Off Balance</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Row className="g-3">
                    <Col md={6}>
                        <label className="form-label" style={{ fontSize: 13 }}>Policy *</label>
                        <select className="form-select form-select-sm" value={policyId} onChange={e => setPolicyId(e.target.value)}>
                            <option value="">Select policy…</option>
                            {policies.map(p => <option key={p.id} value={p.id}>{p.name} ({p.timeOffType})</option>)}
                        </select>
                    </Col>
                    <Col md={6}>
                        <label className="form-label" style={{ fontSize: 13 }}>Opening Balance (hrs)</label>
                        <input type="number" className="form-control form-control-sm" value={balanceHours} onChange={e => setBalanceHours(e.target.value)} placeholder="0" min="0" />
                    </Col>
                    <Col md={12}>
                        <div className="d-flex justify-content-between align-items-center mb-1">
                            <label className="form-label mb-0" style={{ fontSize: 13 }}>
                                Employees *&nbsp;
                                {selectedIds.size > 0 && (
                                    <span className="badge" style={{ background: '#1B75F0', fontSize: 11 }}>{selectedIds.size} selected</span>
                                )}
                            </label>
                        </div>
                        <div className="input-group input-group-sm mb-1">
                            <span className="input-group-text" style={{ fontSize: 12 }}>🔍</span>
                            <input
                                type="text"
                                className="form-control form-control-sm"
                                placeholder="Search employees…"
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                            />
                        </div>
                        <div style={{ border: '1px solid #dee2e6', borderRadius: 6, maxHeight: 220, overflowY: 'auto' }}>
                            <div
                                className="d-flex align-items-center px-3 py-2"
                                style={{ borderBottom: '1px solid #dee2e6', background: '#f8f9fa', cursor: 'pointer', userSelect: 'none' }}
                                onClick={toggleAll}
                            >
                                <input
                                    type="checkbox"
                                    readOnly
                                    checked={allSelected}
                                    style={{ marginRight: 8, cursor: 'pointer', accentColor: '#1B75F0' }}
                                />
                                <span style={{ fontSize: 13, fontWeight: 600 }}>
                                    {allSelected ? 'Deselect All' : 'Select All'}
                                    {search && <span style={{ fontSize: 11, color: '#888', marginLeft: 6 }}>(filtered)</span>}
                                </span>
                            </div>
                            {filtered.length === 0 && (
                                <p className="text-muted text-center py-3 mb-0" style={{ fontSize: 12 }}>No employees match your search.</p>
                            )}
                            {filtered.map(e => (
                                <div
                                    key={e.id}
                                    className="d-flex align-items-center px-3 py-2"
                                    style={{ borderBottom: '1px solid #f0f0f0', cursor: 'pointer', background: selectedIds.has(e.id) ? '#F0F7FF' : 'transparent' }}
                                    onClick={() => toggleOne(e.id)}
                                >
                                    <input
                                        type="checkbox"
                                        readOnly
                                        checked={selectedIds.has(e.id)}
                                        style={{ marginRight: 8, cursor: 'pointer', accentColor: '#1B75F0' }}
                                    />
                                    <span style={{ fontSize: 13 }}>{e.firstName} {e.lastName}</span>
                                </div>
                            ))}
                        </div>
                    </Col>
                </Row>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" size="sm" onClick={onClose} disabled={isPending}>Cancel</Button>
                <Button size="sm" style={{ backgroundColor: '#1B75F0', border: 'none' }}
                    onClick={() => onSave({ employeeIds: [...selectedIds], policyId, balanceHours: Number(balanceHours) || 0 })}
                    disabled={!canSubmit}
                >
                    {isPending ? `Assigning…` : `Assign to ${selectedIds.size || 0} Employee${selectedIds.size !== 1 ? 's' : ''}`}
                </Button>
            </Modal.Footer>
        </Modal>
    );
}

function AdjustBalanceModal({ balance, employeeName, policyName, onClose, onSave }) {
    const [hours, setHours] = useState(String(balance.balanceHours ?? 0));

    return (
        <Modal show onHide={onClose} centered size="sm">
            <Modal.Header closeButton style={{ background: '#1B75F0', color: '#fff' }}>
                <Modal.Title style={{ fontSize: 15 }}>Adjust Balance</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <p style={{ fontSize: 13 }} className="mb-1"><strong>{employeeName}</strong> — {policyName}</p>
                <p style={{ fontSize: 12, color: '#888' }} className="mb-3">Used: {balance.usedHours}h &nbsp;|&nbsp; Pending: {balance.pendingHours}h</p>
                <label className="form-label" style={{ fontSize: 13 }}>New Balance (hrs)</label>
                <input
                    type="number"
                    className="form-control form-control-sm"
                    value={hours}
                    onChange={e => setHours(e.target.value)}
                    onBlur={e => { if (e.target.value === '' || e.target.value === '-') setHours('0'); }}
                    min="0"
                    step="0.5"
                />
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" size="sm" onClick={onClose}>Cancel</Button>
                <Button size="sm" style={{ backgroundColor: '#1B75F0', border: 'none' }} onClick={() => onSave(parseFloat(hours) || 0)}>Save</Button>
            </Modal.Footer>
        </Modal>
    );
}

function NewRequestModal({ policies, employees, onClose, onSave, isPending, errorMsg }) {
    const [form, setForm] = useState({ employeeId: '', policyId: '', startDate: '', endDate: '', totalHours: '', reason: '' });
    const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

    return (
        <Modal show onHide={onClose} centered>
            <Modal.Header closeButton style={{ background: '#1B75F0', color: '#fff' }}>
                <Modal.Title style={{ fontSize: 16 }}>New Time Off Request</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Row className="g-3">
                    {errorMsg && (
                        <Col md={12}>
                            <div className="alert alert-danger py-2 mb-0" style={{ fontSize: 12 }}>{errorMsg}</div>
                        </Col>
                    )}
                    <Col md={12}>
                        <label className="form-label" style={{ fontSize: 13 }}>Employee *</label>
                        <select className="form-select form-select-sm" value={form.employeeId} onChange={e => set('employeeId', e.target.value)}>
                            <option value="">Select employee…</option>
                            {employees.map(e => <option key={e.id} value={e.id}>{e.firstName} {e.lastName}</option>)}
                        </select>
                    </Col>
                    <Col md={12}>
                        <label className="form-label" style={{ fontSize: 13 }}>Policy / Type *</label>
                        <select className="form-select form-select-sm" value={form.policyId} onChange={e => set('policyId', e.target.value)}>
                            <option value="">Select policy…</option>
                            {policies.map(p => <option key={p.id} value={p.id}>{p.name} ({p.timeOffType})</option>)}
                        </select>
                    </Col>
                    <Col md={6}>
                        <label className="form-label" style={{ fontSize: 13 }}>Start Date *</label>
                        <input type="date" className="form-control form-control-sm" value={form.startDate} onChange={e => set('startDate', e.target.value)} />
                    </Col>
                    <Col md={6}>
                        <label className="form-label" style={{ fontSize: 13 }}>End Date *</label>
                        <input type="date" className="form-control form-control-sm" value={form.endDate} onChange={e => set('endDate', e.target.value)} />
                    </Col>
                    <Col md={4}>
                        <label className="form-label" style={{ fontSize: 13 }}>Total Hours *</label>
                        <input type="number" className="form-control form-control-sm" value={form.totalHours} onChange={e => set('totalHours', e.target.value)} placeholder="8" />
                    </Col>
                    <Col md={12}>
                        <label className="form-label" style={{ fontSize: 13 }}>Reason</label>
                        <input className="form-control form-control-sm" value={form.reason} onChange={e => set('reason', e.target.value)} placeholder="Optional" />
                    </Col>
                </Row>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" size="sm" onClick={onClose} disabled={isPending}>Cancel</Button>
                <Button size="sm" style={{ backgroundColor: '#1B75F0', border: 'none' }}
                    onClick={() => onSave({ ...form, totalHours: Number(form.totalHours) })}
                    disabled={!form.employeeId || !form.policyId || !form.startDate || !form.endDate || !form.totalHours || isPending}
                >
                    {isPending ? 'Submitting…' : 'Submit Request'}
                </Button>
            </Modal.Footer>
        </Modal>
    );
}

function ReviewModal({ request, action, employeeName, policyName, onClose, onSave }) {
    const [note, setNote] = useState('');
    return (
        <Modal show onHide={onClose} centered size="sm">
            <Modal.Header closeButton style={{ background: action === 'approve' ? '#198754' : '#dc3545', color: '#fff' }}>
                <Modal.Title style={{ fontSize: 15 }}>{action === 'approve' ? 'Approve' : 'Deny'} Request</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <p style={{ fontSize: 13 }}><strong>{employeeName}</strong> — {policyName}</p>
                <p style={{ fontSize: 12, color: '#555' }}>{fmtDate(request.startDate)} – {fmtDate(request.endDate)} &nbsp;({request.totalHours}h)</p>
                <label className="form-label" style={{ fontSize: 13 }}>Note (optional)</label>
                <input className="form-control form-control-sm" value={note} onChange={e => setNote(e.target.value)} />
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" size="sm" onClick={onClose}>Cancel</Button>
                <Button size="sm"
                    style={{ backgroundColor: action === 'approve' ? '#198754' : '#dc3545', border: 'none' }}
                    onClick={() => onSave(note)}
                >
                    {action === 'approve' ? 'Approve' : 'Deny'}
                </Button>
            </Modal.Footer>
        </Modal>
    );
}

function TimeOff() {
    const qc = useQueryClient();
    const companyId = localStorage.getItem("companyId") || ENV.COMPANY_ID;
    const [activeTab, setActiveTab] = useState(0);

    const [policyModal, setPolicyModal] = useState(null);
    const [assignModal, setAssignModal] = useState(false);
    const [adjustModal, setAdjustModal] = useState(null);
    const [requestModal, setRequestModal] = useState(false);
    const [reviewModal, setReviewModal] = useState(null);
    const [balanceSearch, setBalanceSearch] = useState('');
    const [holidayModal, setHolidayModal] = useState(null); // null | 'new' | holiday object
    const [importConfirm, setImportConfirm] = useState(false);
    const [confirmDeleteReq, setConfirmDeleteReq] = useState(null);   // request id
    const [importYear, setImportYear] = useState(new Date().getFullYear());
    const [deleteHolidayConfirm, setDeleteHolidayConfirm] = useState(null); // holiday object to delete

    const { data: policiesRaw = [], isLoading: loadPol } = useQuery({ queryKey: ['time-off-policies'], queryFn: getPolicies });
    const { data: balancesRaw = [], isLoading: loadBal } = useQuery({ queryKey: ['time-off-balances'], queryFn: () => getBalances() });
    const { data: requestsRaw = [], isLoading: loadReq } = useQuery({ queryKey: ['time-off-requests'], queryFn: () => getRequests() });
    const { data: employeesRaw = [] } = useQuery({ queryKey: ['employees'], queryFn: getEmployees });
    const { data: holidaysRaw = [], isLoading: loadHolidays } = useQuery({ queryKey: ['company-holidays'], queryFn: getHolidays });

    const policies = useMemo(() => Array.isArray(policiesRaw) ? policiesRaw : [], [policiesRaw]);
    const balances = useMemo(() => Array.isArray(balancesRaw) ? balancesRaw : [], [balancesRaw]);
    const requests = useMemo(() => Array.isArray(requestsRaw) ? requestsRaw : [], [requestsRaw]);
    const employees = useMemo(() => unwrap(employeesRaw), [employeesRaw]);

    const empMap = useMemo(() => {
        const m = {};
        employees.forEach(e => { m[e.id] = e; });
        return m;
    }, [employees]);

    const polMap = useMemo(() => {
        const m = {};
        policies.forEach(p => { m[p.id] = p; });
        return m;
    }, [policies]);

    const empName = (id) => {
        const e = empMap[id];
        return e ? `${e.firstName} ${e.lastName}` : id?.slice(0, 8) + '…';
    };
    const polName = (id) => polMap[id]?.name ?? '—';

    const invalidate = (keys) => keys.forEach(k => qc.invalidateQueries({ queryKey: [k] }));

    const [deletingPolicyId, setDeletingPolicyId] = useState(null);
    const [deletePolicyError, setDeletePolicyError] = useState(null);
    const [deleteConfirmModal, setDeleteConfirmModal] = useState(null); // { policyId, policyName, balanceCount, requestCount }

    const mutCreatePolicy = useMutation({ mutationFn: createPolicy, onSuccess: () => { invalidate(['time-off-policies']); setPolicyModal(null); } });
    const mutUpdatePolicy = useMutation({ mutationFn: updatePolicy, onSuccess: () => { invalidate(['time-off-policies']); setPolicyModal(null); } });

    const handleDeletePolicy = (policyId) => {
        const policy = polMap[policyId];
        const linkedBalances = balances.filter(b => b.policyId === policyId);
        const linkedRequests = requests.filter(r => r.policyId === policyId);
        setDeleteConfirmModal({
            policyId,
            policyName: policy?.name ?? 'this policy',
            balanceCount: linkedBalances.length,
            requestCount: linkedRequests.length,
        });
    };

    const confirmDeletePolicy = async () => {
        if (!deleteConfirmModal) return;
        const { policyId } = deleteConfirmModal;
        const linkedBalances = balances.filter(b => b.policyId === policyId);
        const linkedRequests = requests.filter(r => r.policyId === policyId);
        setDeleteConfirmModal(null);
        setDeletingPolicyId(policyId);
        setDeletePolicyError(null);
        try {
            await Promise.all(linkedRequests.map(r => deleteRequest(r.id)));
            await Promise.all(linkedBalances.map(b => deleteBalance(b.id)));
            await deletePolicy(policyId);
            invalidate(['time-off-policies', 'time-off-balances', 'time-off-requests']);
        } catch (err) {
            setDeletePolicyError(err?.response?.data?.message || err?.response?.data?.title || err?.message || 'Failed to delete policy.');
        } finally {
            setDeletingPolicyId(null);
        }
    };

    const policyMutPending = mutCreatePolicy.isPending || mutUpdatePolicy.isPending;
    const policyMutError = mutCreatePolicy.error || mutUpdatePolicy.error;
    const policyErrorMsg = policyMutError
        ? (policyMutError?.response?.data?.message || policyMutError?.response?.data?.title || policyMutError?.message || 'Failed to save policy. Please try again.')
        : null;

    const [assignPending, setAssignPending] = useState(false);
    const handleBatchAssign = async ({ employeeIds, policyId, balanceHours }) => {
        setAssignPending(true);
        try {
            await Promise.all(employeeIds.map(employeeId => createBalance({ employeeId, policyId, balanceHours })));
            invalidate(['time-off-balances']);
            setAssignModal(false);
        } finally {
            setAssignPending(false);
        }
    };
    const mutAdjust = useMutation({ mutationFn: adjustBalance, onSuccess: () => { invalidate(['time-off-balances']); setAdjustModal(null); } });

    const mutNewReq = useMutation({
        mutationFn: createRequest,
        onSuccess: () => { invalidate(['time-off-requests', 'time-off-balances']); setRequestModal(false); },
    });
    const mutApprove = useMutation({
        mutationFn: approveRequest,
        onSuccess: (_, vars) => {
            invalidate(['time-off-requests', 'time-off-balances']);
            const req = reviewModal?.request;
            if (req?.employeeId) {
                const pol = policies.find(p => p.id === req.policyId);
                createNotification({
                    recipientType: 'employee',
                    recipientId: req.employeeId,
                    type: 'time_off_approved',
                    title: 'Time Off Request Approved',
                    message: `Your ${pol?.timeOffType ?? 'time off'} request for ${req.totalHours} hrs has been approved.`,
                    link: '/employee/time-off',
                }).catch(() => {});
            }
            setReviewModal(null);
        },
    });
    const mutDeny = useMutation({
        mutationFn: denyRequest,
        onSuccess: () => {
            invalidate(['time-off-requests', 'time-off-balances']);
            const req = reviewModal?.request;
            if (req?.employeeId) {
                const pol = policies.find(p => p.id === req.policyId);
                createNotification({
                    recipientType: 'employee',
                    recipientId: req.employeeId,
                    type: 'time_off_denied',
                    title: 'Time Off Request Denied',
                    message: `Your ${pol?.timeOffType ?? 'time off'} request for ${req.totalHours} hrs has been denied.`,
                    link: '/employee/time-off',
                }).catch(() => {});
            }
            setReviewModal(null);
        },
    });
    const mutDeleteReq = useMutation({ mutationFn: deleteRequest, onSuccess: () => invalidate(['time-off-requests', 'time-off-balances']) });

    const holidays = useMemo(() => Array.isArray(holidaysRaw) ? holidaysRaw : [], [holidaysRaw]);
    const mutCreateHoliday = useMutation({ mutationFn: createHoliday, onSuccess: () => { invalidate(['company-holidays']); setHolidayModal(null); } });
    const mutUpdateHoliday = useMutation({ mutationFn: updateHoliday, onSuccess: () => { invalidate(['company-holidays']); setHolidayModal(null); } });
    const mutDeleteHoliday = useMutation({ mutationFn: deleteHoliday, onSuccess: () => invalidate(['company-holidays']) });

    const handleImportUSHolidays = async () => {
        const holidays = getUSFederalHolidays(importYear);
        await Promise.all(holidays.map(h => createHoliday({ ...h, appliesTo: 'salaried', isActive: true })));
        invalidate(['company-holidays']);
        setImportConfirm(false);
    };

    const pendingCount = requests.filter(r => r.status === 'pending').length;

    return (
        <div className="content" style={{ padding: '16px 24px' }}>
            <div className="start-new-payroll-section">
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <div>
                        <h5 className="fw-bold mb-0">Time Off Management</h5>
                        <p className="text-muted mb-0" style={{ fontSize: 13 }}>Track vacation, sick, holiday, PTO and personal time</p>
                    </div>
                    {pendingCount > 0 && (
                        <span className="badge" style={{ background: '#1B75F0', fontSize: 13, padding: '6px 12px' }}>
                            {pendingCount} pending request{pendingCount > 1 ? 's' : ''}
                        </span>
                    )}
                </div>

                <div className="row g-3 mb-4">
                    {[
                        { label: 'Policies', value: policies.length, color: '#0d6efd' },
                        { label: 'Employees with Balances', value: [...new Set(balances.map(b => b.employeeId))].length, color: '#198754' },
                        { label: 'Pending Requests', value: pendingCount, color: '#1B75F0' },
                        { label: 'Approved This Year', value: requests.filter(r => r.status === 'approved').length, color: '#6f42c1' },
                    ].map(card => (
                        <div key={card.label} className="col-6 col-md-3">
                            <div style={{ background: '#f8f9fa', borderRadius: 8, padding: '12px 16px', borderLeft: `4px solid ${card.color}` }}>
                                <p className="mb-1" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 1, color: '#888' }}>{card.label}</p>
                                <p className="mb-0 fw-bold" style={{ fontSize: 22, color: card.color }}>{card.value}</p>
                            </div>
                        </div>
                    ))}
                </div>

                <div className="d-flex align-items-center justify-content-between mb-3">
                    <ul className="d-flex gap-3 tab" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                        {TABS.map((tab, i) => (
                            <li key={tab}
                                className={`nav-link${activeTab === i ? ' active' : ''}`}
                                style={{ cursor: 'pointer' }}
                                onClick={() => setActiveTab(i)}
                            >
                                {tab}
                                {tab === 'Requests' && pendingCount > 0 && (
                                    <span className="ms-2 badge rounded-pill" style={{ background: '#1B75F0', fontSize: 10 }}>{pendingCount}</span>
                                )}
                            </li>
                        ))}
                    </ul>
                    <div>
                        {activeTab === 0 && (
                            <button className="button-orange px-3" style={{ fontSize: 13 }} onClick={() => setRequestModal(true)}>+ New Request</button>
                        )}
                        {activeTab === 1 && (
                            <button className="button-orange px-3" style={{ fontSize: 13 }} onClick={() => setAssignModal(true)}>+ Assign Balance</button>
                        )}
                        {activeTab === 2 && (
                            <button className="button-orange px-3" style={{ fontSize: 13 }} onClick={() => setPolicyModal('new')}>+ Add Policy</button>
                        )}
                        {activeTab === 3 && (
                            <div style={{ display: 'flex', gap: 8 }}>
                                <button className="btn btn-sm btn-outline-secondary" style={{ fontSize: 13 }} onClick={() => setImportConfirm(true)}>Import US Federal Holidays</button>
                                <button className="button-orange px-3" style={{ fontSize: 13 }} onClick={() => setHolidayModal('new')}>+ Add Holiday</button>
                            </div>
                        )}
                    </div>
                </div>

                {activeTab === 0 && (
                    loadReq ? <p className="text-muted text-center py-4">Loading...</p> :
                    requests.length === 0 ? <p className="text-muted text-center py-4">No time off requests yet.</p> :
                    <div style={{ overflowX: 'auto' }}>
                        <table className="table table-sm table-hover" style={{ fontSize: 13 }}>
                            <thead style={{ background: '#f8f9fa' }}>
                                <tr>
                                    <th>Employee</th>
                                    <th>Type</th>
                                    <th>Start</th>
                                    <th>End</th>
                                    <th className="text-center">Hours</th>
                                    <th>Reason</th>
                                    <th className="text-center">Status</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {requests.map(r => (
                                    <tr key={r.id}>
                                        <td className="fw-medium">{empName(r.employeeId)}</td>
                                        <td>{polMap[r.policyId]?.timeOffType ?? '—'}</td>
                                        <td>{fmtDate(r.startDate)}</td>
                                        <td>{fmtDate(r.endDate)}</td>
                                        <td className="text-center">{r.totalHours}</td>
                                        <td style={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.reason || '—'}</td>
                                        <td className="text-center">
                                            <span className="badge" style={{ ...(STATUS_COLORS[r.status] ?? {}), fontSize: 11, padding: '3px 8px' }}>
                                                {r.status}
                                            </span>
                                        </td>
                                        <td>
                                            <div className="d-flex gap-1 justify-content-end">
                                                {r.status === 'pending' && (<>
                                                    <button className="btn btn-sm btn-success" style={{ fontSize: 11, padding: '2px 8px' }}
                                                        onClick={() => setReviewModal({ request: r, action: 'approve' })}>Approve</button>
                                                    <button className="btn btn-sm btn-danger" style={{ fontSize: 11, padding: '2px 8px' }}
                                                        onClick={() => setReviewModal({ request: r, action: 'deny' })}>Deny</button>
                                                </>)}
                                                <button className="btn btn-sm btn-outline-secondary" style={{ fontSize: 11, padding: '2px 6px' }}
                                                    onClick={() => setConfirmDeleteReq(r.id)}>✕</button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {activeTab === 1 && (() => {
                    const TYPE_COLORS = {
                        Vacation: { bg: '#dbeafe', color: '#1d4ed8' },
                        Sick: { bg: '#fce7f3', color: '#be185d' },
                        Holiday: { bg: '#d1fae5', color: '#065f46' },
                        Personal: { bg: '#ede9fe', color: '#5b21b6' },
                        PTO: { bg: '#EFF6FF', color: '#c2410c' },
                    };
                    const typeStyle = (t) => TYPE_COLORS[t] || { bg: '#f1f5f9', color: '#475569' };

                    const q = balanceSearch.trim().toLowerCase();
                    const grouped = {};
                    balances.forEach(b => {
                        const name = empName(b.employeeId);
                        if (q && !name.toLowerCase().includes(q) && !(polMap[b.policyId]?.timeOffType || '').toLowerCase().includes(q)) return;
                        if (!grouped[b.employeeId]) grouped[b.employeeId] = { name, items: [] };
                        grouped[b.employeeId].items.push(b);
                    });
                    const groupedArr = Object.values(grouped);

                    const getInitials = (name) => name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
                    const avatarColors = ['#1B75F0', '#0d6efd', '#198754', '#6f42c1', '#dc3545', '#0dcaf0', '#1B75F0', '#20c997'];
                    const avatarColor = (name) => avatarColors[name.charCodeAt(0) % avatarColors.length];

                    return loadBal ? <p className="text-muted text-center py-4">Loading...</p> :
                    balances.length === 0 ? (
                        <div className="text-center py-5">
                            <div style={{ fontSize: 40, marginBottom: 8 }}>📋</div>
                            <p className="text-muted mb-1">No balances assigned yet.</p>
                            <p className="text-muted" style={{ fontSize: 12 }}>Use "+ Assign Balance" to add one.</p>
                        </div>
                    ) : (
                        <div>
                            <div className="input-group input-group-sm mb-3" style={{ maxWidth: 320 }}>
                                <span className="input-group-text" style={{ background: '#f8f9fa', border: '1px solid #dee2e6' }}>🔍</span>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Search by employee or type…"
                                    value={balanceSearch}
                                    onChange={e => setBalanceSearch(e.target.value)}
                                    style={{ fontSize: 13 }}
                                />
                                {balanceSearch && (
                                    <button className="btn btn-outline-secondary btn-sm" onClick={() => setBalanceSearch('')} style={{ fontSize: 12 }}>✕</button>
                                )}
                            </div>
                            {groupedArr.length === 0 ? (
                                <p className="text-muted text-center py-4" style={{ fontSize: 13 }}>No results match your search.</p>
                            ) : (
                                <div className="d-flex flex-column gap-3">
                                    {groupedArr.map(({ name, items }) => (
                                        <div key={name} style={{ border: '1px solid #e9ecef', borderRadius: 10, overflow: 'hidden', background: '#fff' }}>
                                            <div className="d-flex align-items-center px-4 py-3" style={{ background: '#f8f9fa', borderBottom: '1px solid #e9ecef' }}>
                                                <div style={{
                                                    width: 36, height: 36, borderRadius: '50%',
                                                    background: avatarColor(name), color: '#fff',
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                    fontSize: 13, fontWeight: 700, marginRight: 12, flexShrink: 0,
                                                }}>
                                                    {getInitials(name)}
                                                </div>
                                                <div>
                                                    <div className="fw-semibold" style={{ fontSize: 14 }}>{name}</div>
                                                    <div style={{ fontSize: 11, color: '#888' }}>{items.length} time off {items.length === 1 ? 'policy' : 'policies'}</div>
                                                </div>
                                                <div className="ms-auto d-flex gap-4">
                                                    <div className="text-center">
                                                        <div style={{ fontSize: 10, color: '#888', textTransform: 'uppercase', letterSpacing: 0.8 }}>Total Balance</div>
                                                        <div className="fw-bold" style={{ fontSize: 15, color: '#198754' }}>
                                                            {parseFloat(items.reduce((s, b) => s + (b.balanceHours || 0), 0).toFixed(2))}h
                                                        </div>
                                                    </div>
                                                    <div className="text-center">
                                                        <div style={{ fontSize: 10, color: '#888', textTransform: 'uppercase', letterSpacing: 0.8 }}>Used</div>
                                                        <div className="fw-bold" style={{ fontSize: 15, color: '#dc3545' }}>
                                                            {parseFloat(items.reduce((s, b) => s + (b.usedHours || 0), 0).toFixed(2))}h
                                                        </div>
                                                    </div>
                                                    <div className="text-center">
                                                        <div style={{ fontSize: 10, color: '#888', textTransform: 'uppercase', letterSpacing: 0.8 }}>Pending</div>
                                                        <div className="fw-bold" style={{ fontSize: 15, color: '#1B75F0' }}>
                                                            {parseFloat(items.reduce((s, b) => s + (b.pendingHours || 0), 0).toFixed(2))}h
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            <div>
                                                <table className="table table-sm mb-0" style={{ fontSize: 13 }}>
                                                    <thead>
                                                        <tr style={{ background: '#fcfcfc' }}>
                                                            <th style={{ paddingLeft: 20, color: '#888', fontWeight: 500, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.8 }}>Policy</th>
                                                            <th style={{ color: '#888', fontWeight: 500, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.8 }}>Type</th>
                                                            <th className="text-center" style={{ color: '#888', fontWeight: 500, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.8 }}>Balance</th>
                                                            <th className="text-center" style={{ color: '#888', fontWeight: 500, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.8 }}>Used</th>
                                                            <th className="text-center" style={{ color: '#888', fontWeight: 500, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.8 }}>Pending</th>
                                                            <th className="text-center" style={{ color: '#888', fontWeight: 500, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.8 }}>YTD Accrued</th>
                                                            <th></th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {items.map(b => {
                                                            const pol = polMap[b.policyId];
                                                            const ttype = pol?.timeOffType ?? '—';
                                                            const ts = typeStyle(ttype);
                                                            const maxHrs = pol?.maxAccrualHours || 0;
                                                            const pct = maxHrs > 0 ? Math.min(100, Math.round(((b.balanceHours || 0) / maxHrs) * 100)) : null;
                                                            return (
                                                                <tr key={b.id} style={{ verticalAlign: 'middle' }}>
                                                                    <td style={{ paddingLeft: 20 }}>
                                                                        <div className="fw-medium">{pol?.name ?? '—'}</div>
                                                                        {pct !== null && (
                                                                            <div style={{ marginTop: 3 }}>
                                                                                <div style={{ height: 4, background: '#e9ecef', borderRadius: 2, width: 100 }}>
                                                                                    <div style={{ height: 4, borderRadius: 2, width: `${pct}%`, background: ts.color }} />
                                                                                </div>
                                                                                <div style={{ fontSize: 10, color: '#aaa', marginTop: 1 }}>{pct}% of {maxHrs}h max</div>
                                                                            </div>
                                                                        )}
                                                                    </td>
                                                                    <td>
                                                                        <span style={{ ...ts, fontSize: 11, padding: '2px 8px', borderRadius: 20, fontWeight: 600 }}>
                                                                            {ttype}
                                                                        </span>
                                                                    </td>
                                                    <td className="text-center">
                                                        <span className="fw-semibold" style={{ color: (b.balanceHours || 0) > 0 ? '#198754' : '#dc3545', fontSize: 13 }}>
                                                            {parseFloat((b.balanceHours ?? 0).toFixed(2))}h
                                                        </span>
                                                    </td>
                                                    <td className="text-center" style={{ color: '#dc3545', fontSize: 13 }}>{parseFloat((b.usedHours ?? 0).toFixed(2))}h</td>
                                                    <td className="text-center" style={{ color: '#1B75F0', fontSize: 13 }}>{parseFloat((b.pendingHours ?? 0).toFixed(2))}h</td>
                                                    <td className="text-center text-muted" style={{ fontSize: 13 }}>{parseFloat((b.yearToDateAccruedHours ?? 0).toFixed(2))}h</td>
                                                                    <td className="text-end pe-3">
                                                                        <button
                                                                            className="btn btn-sm"
                                                                            style={{ fontSize: 11, padding: '2px 10px', border: '1px solid #dee2e6', background: '#fff', color: '#444', borderRadius: 6 }}
                                                                            onClick={() => setAdjustModal(b)}
                                                                        >
                                                                            Adjust
                                                                        </button>
                                                                    </td>
                                                                </tr>
                                                            );
                                                        })}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    );
                })()}

                {activeTab === 2 && (
                    loadPol ? <p className="text-muted text-center py-4">Loading...</p> :
                    policies.length === 0 ? <p className="text-muted text-center py-4">No policies yet. Use "Add Policy" to create one.</p> :
                    <div style={{ overflowX: 'auto' }}>
                    {deletePolicyError && (
                        <div className="alert alert-danger d-flex align-items-center justify-content-between py-2 px-3 mb-2" style={{ fontSize: 13 }}>
                            <span>{deletePolicyError}</span>
                            <button type="button" className="btn-close btn-close-sm" onClick={() => setDeletePolicyError(null)} />
                        </div>
                    )}
                        <table className="table table-sm table-hover" style={{ fontSize: 13 }}>
                            <thead style={{ background: '#f8f9fa' }}>
                                <tr>
                                    <th>Policy Name</th>
                                    <th>Type</th>
                                    <th>Accrual Method</th>
                                    <th className="text-center">Hrs / Period</th>
                                    <th className="text-center">Max Accrual</th>
                                    <th className="text-center">Max Carryover</th>
                                    <th className="text-center">Status</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {policies.map(p => (
                                    <tr key={p.id}>
                                        <td className="fw-medium">{p.name}</td>
                                        <td>{p.timeOffType}</td>
                                        <td style={{ textTransform: 'capitalize' }}>{(p.accrualMethod || '').replace(/_/g, ' ')}</td>
                                        <td className="text-center">{p.accrualRatePerPeriod ?? '—'}</td>
                                        <td className="text-center">{p.maxAccrualHours ? `${p.maxAccrualHours}h` : '—'}</td>
                                        <td className="text-center">{p.maxCarryoverHours ? `${p.maxCarryoverHours}h` : '—'}</td>
                                        <td className="text-center">
                                            <span className="badge" style={{ background: p.isActive ? '#d1e7dd' : '#e9ecef', color: p.isActive ? '#0a3622' : '#666', fontSize: 11 }}>
                                                {p.isActive ? 'Active' : 'Inactive'}
                                            </span>
                                        </td>
                                        <td>
                                            <div className="d-flex gap-1 justify-content-end">
                                                <button className="btn btn-sm btn-outline-secondary" style={{ fontSize: 11, padding: '2px 8px' }}
                                                    onClick={() => setPolicyModal(p)}>Edit</button>
                                                <button className="btn btn-sm btn-outline-danger" style={{ fontSize: 11, padding: '2px 6px' }}
                                                    disabled={deletingPolicyId === p.id}
                                                    onClick={() => handleDeletePolicy(p.id)}>
                                                    {deletingPolicyId === p.id ? '…' : '✕'}
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {activeTab === 3 && (
                    loadHolidays ? <p className="text-muted text-center py-4">Loading...</p> : (
                    <div>
                        {holidays.length === 0 && (
                            <div style={{ textAlign: 'center', padding: '40px 20px', color: '#888' }}>
                                <div style={{ fontSize: 40, marginBottom: 12 }}>🗓️</div>
                                <p style={{ fontSize: 14, marginBottom: 8 }}>No holidays set yet.</p>
                                <p style={{ fontSize: 13 }}>Click <strong>"Import US Holidays 2026"</strong> to pre-populate common US holidays, or add them manually.</p>
                            </div>
                        )}
                        {holidays.length > 0 && (
                            <div style={{ overflowX: 'auto' }}>
                                <table className="table table-sm table-hover" style={{ fontSize: 13 }}>
                                    <thead style={{ background: '#f8f9fa' }}>
                                        <tr>
                                            <th>Holiday Name</th>
                                            <th>Date</th>
                                            <th className="text-center">Hours</th>
                                            <th className="text-center">Applies To</th>
                                            <th className="text-center">Status</th>
                                            <th></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {holidays.map(h => (
                                            <tr key={h.id}>
                                                <td style={{ fontWeight: 600 }}>🎉 {h.name}</td>
                                                <td>{fmtDate(h.date)}</td>
                                                <td className="text-center">{h.hours}h</td>
                                                <td className="text-center">
                                                    <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 10, background: h.appliesTo === 'all' ? '#e3f2fd' : '#fff3e0', color: h.appliesTo === 'all' ? '#1565c0' : '#e65100' }}>
                                                        {h.appliesTo === 'all' ? 'All employees' : 'Salaried only'}
                                                    </span>
                                                </td>
                                                <td className="text-center">
                                                    <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 10, background: h.isActive ? '#e8f5e9' : '#f5f5f5', color: h.isActive ? '#2e7d32' : '#999' }}>
                                                        {h.isActive ? 'Active' : 'Inactive'}
                                                    </span>
                                                </td>
                                                <td className="text-end pe-2">
                                                    <button className="btn btn-sm me-1" style={{ fontSize: 11, border: '1px solid #dee2e6', borderRadius: 5, padding: '2px 10px' }} onClick={() => setHolidayModal(h)}>Edit</button>
                                                    <button className="btn btn-sm" style={{ fontSize: 11, border: '1px solid #f5c2c7', borderRadius: 5, padding: '2px 10px', color: '#dc3545' }} onClick={() => setDeleteHolidayConfirm(h)}>Delete</button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                    )
                )}
            </div>

            {holidayModal && (
                <HolidayModal
                    holiday={holidayModal === 'new' ? null : holidayModal}
                    onClose={() => setHolidayModal(null)}
                    onSave={(data) => holidayModal === 'new'
                        ? mutCreateHoliday.mutate(data)
                        : mutUpdateHoliday.mutate({ id: holidayModal.id, ...data })
                    }
                />
            )}

            {deleteHolidayConfirm && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1060, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <div style={{ background: '#fff', borderRadius: 12, width: 400, boxShadow: '0 8px 32px rgba(0,0,0,0.18)', overflow: 'hidden' }}>
                        <div style={{ background: '#fef2f2', borderBottom: '1px solid #fecaca', padding: '16px 20px', display: 'flex', alignItems: 'center', gap: 12 }}>
                            <div style={{ width: 36, height: 36, borderRadius: '50%', background: '#dc3545', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                <span style={{ color: '#fff', fontSize: 18, fontWeight: 700 }}>!</span>
                            </div>
                            <div>
                                <div style={{ fontWeight: 700, fontSize: 15 }}>Delete Holiday</div>
                                <div style={{ fontSize: 12, color: '#666', marginTop: 1 }}>This action cannot be undone</div>
                            </div>
                        </div>
                        <div style={{ padding: '20px 24px' }}>
                            <p style={{ fontSize: 14, color: '#333', marginBottom: 4 }}>
                                Are you sure you want to delete <strong>"{deleteHolidayConfirm.name}"</strong>?
                            </p>
                            <p style={{ fontSize: 13, color: '#888', marginBottom: 0 }}>
                                📅 {fmtDate(deleteHolidayConfirm.date)} &nbsp;·&nbsp; {deleteHolidayConfirm.hours}h &nbsp;·&nbsp; {deleteHolidayConfirm.appliesTo === 'all' ? 'All employees' : 'Salaried only'}
                            </p>
                        </div>
                        <div style={{ padding: '12px 24px 20px', display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                            <button onClick={() => setDeleteHolidayConfirm(null)} style={{ padding: '7px 20px', borderRadius: 6, border: '1px solid #ccc', background: '#fff', cursor: 'pointer', fontSize: 13 }}>Cancel</button>
                            <button onClick={() => { mutDeleteHoliday.mutate(deleteHolidayConfirm.id); setDeleteHolidayConfirm(null); }}
                                style={{ padding: '7px 20px', borderRadius: 6, border: 'none', background: '#dc3545', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {importConfirm && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1060, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <div style={{ background: '#fff', borderRadius: 12, padding: 28, width: 420, boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}>
                        <h6 style={{ fontWeight: 700, marginBottom: 8 }}>Import US Federal Holidays</h6>
                        <p style={{ fontSize: 13, color: '#555', marginBottom: 16 }}>
                            Adds all 11 standard US federal holidays (8 hrs each, salaried employees). You can edit or delete them after importing.
                        </p>
                        <div style={{ marginBottom: 20 }}>
                            <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 6 }}>Select Year</label>
                            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                {[new Date().getFullYear() - 1, new Date().getFullYear(), new Date().getFullYear() + 1, new Date().getFullYear() + 2].map(y => (
                                    <button key={y} onClick={() => setImportYear(y)}
                                        style={{ padding: '6px 16px', borderRadius: 6, border: `2px solid ${importYear === y ? '#1B75F0' : '#dee2e6'}`, background: importYear === y ? '#fff3e0' : '#fff', color: importYear === y ? '#1B75F0' : '#333', cursor: 'pointer', fontSize: 13, fontWeight: importYear === y ? 700 : 400 }}>
                                        {y}
                                    </button>
                                ))}
                            </div>
                            <p style={{ fontSize: 12, color: '#888', marginTop: 8 }}>
                                Will generate correct dates for {importYear} (e.g. Memorial Day = {toDateStr(getNthWeekday(importYear, 4, 1, -1))}, Thanksgiving = {toDateStr(getNthWeekday(importYear, 10, 4, 4))})
                            </p>
                        </div>
                        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                            <button onClick={() => setImportConfirm(false)} style={{ padding: '7px 18px', borderRadius: 6, border: '1px solid #ccc', background: '#fff', cursor: 'pointer', fontSize: 13 }}>Cancel</button>
                            <button onClick={handleImportUSHolidays} style={{ padding: '7px 18px', borderRadius: 6, border: 'none', background: '#1B75F0', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                                Import {importYear} Holidays
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {policyModal && (
                <PolicyModal
                    policy={policyModal === 'new' ? null : policyModal}
                    onClose={() => setPolicyModal(null)}
                    onSave={(data) => policyModal === 'new'
                        ? mutCreatePolicy.mutate(data)
                        : mutUpdatePolicy.mutate({ id: policyModal.id, ...data })
                    }
                    isPending={policyMutPending}
                    errorMsg={policyErrorMsg}
                />
            )}

            {assignModal && (
                <AssignBalanceModal
                    policies={policies.filter(p => p.isActive)}
                    employees={employees}
                    onClose={() => setAssignModal(false)}
                    onSave={handleBatchAssign}
                    isPending={assignPending}
                />
            )}

            {adjustModal && (
                <AdjustBalanceModal
                    balance={adjustModal}
                    employeeName={empName(adjustModal.employeeId)}
                    policyName={polName(adjustModal.policyId)}
                    onClose={() => setAdjustModal(null)}
                    onSave={(hrs) => mutAdjust.mutate({ id: adjustModal.id, balanceHours: hrs })}
                />
            )}

            {requestModal && (
                <NewRequestModal
                    policies={policies.filter(p => p.isActive)}
                    employees={employees}
                    onClose={() => setRequestModal(false)}
                    onSave={(data) => mutNewReq.mutate(data)}
                    isPending={mutNewReq.isPending}
                    errorMsg={mutNewReq.error ? (mutNewReq.error?.response?.data?.message || mutNewReq.error?.message || 'Failed to submit request.') : null}
                />
            )}

            {reviewModal && (
                <ReviewModal
                    request={reviewModal.request}
                    action={reviewModal.action}
                    employeeName={empName(reviewModal.request.employeeId)}
                    policyName={polName(reviewModal.request.policyId)}
                    onClose={() => setReviewModal(null)}
                    onSave={(note) => reviewModal.action === 'approve'
                        ? mutApprove.mutate({ id: reviewModal.request.id, reviewNote: note })
                        : mutDeny.mutate({ id: reviewModal.request.id, reviewNote: note })
                    }
                />
            )}

            {deleteConfirmModal && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1060, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <div style={{ background: '#fff', borderRadius: 12, width: 420, boxShadow: '0 8px 32px rgba(0,0,0,0.18)', overflow: 'hidden' }}>
                        {/* Header */}
                        <div style={{ background: '#fff1eb', borderBottom: '1px solid #f5cbb0', padding: '16px 20px', display: 'flex', alignItems: 'center', gap: 10 }}>
                            <div style={{ width: 36, height: 36, borderRadius: '50%', background: '#1B75F0', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                <span style={{ color: '#fff', fontSize: 18, fontWeight: 700 }}>!</span>
                            </div>
                            <div>
                                <div style={{ fontWeight: 700, fontSize: 15, color: '#1a1a1a' }}>Delete Policy</div>
                                <div style={{ fontSize: 12, color: '#666', marginTop: 1 }}>This action cannot be undone</div>
                            </div>
                        </div>
                        {/* Body */}
                        <div style={{ padding: '20px 24px' }}>
                            <p style={{ fontSize: 14, color: '#333', marginBottom: 12 }}>
                                You are about to permanently delete <strong>"{deleteConfirmModal.policyName}"</strong>.
                            </p>
                            {(deleteConfirmModal.balanceCount > 0 || deleteConfirmModal.requestCount > 0) && (
                                <div style={{ background: '#fff8f0', border: '1px solid #f5cbb0', borderRadius: 8, padding: '10px 14px', marginBottom: 4 }}>
                                    <div style={{ fontSize: 13, fontWeight: 600, color: '#c45c00', marginBottom: 6 }}>The following linked records will also be deleted:</div>
                                    <div style={{ display: 'flex', gap: 16 }}>
                                        {deleteConfirmModal.balanceCount > 0 && (
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#555' }}>
                                                <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#1B75F0', display: 'inline-block' }} />
                                                <strong>{deleteConfirmModal.balanceCount}</strong> employee balance{deleteConfirmModal.balanceCount !== 1 ? 's' : ''}
                                            </div>
                                        )}
                                        {deleteConfirmModal.requestCount > 0 && (
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#555' }}>
                                                <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#dc3545', display: 'inline-block' }} />
                                                <strong>{deleteConfirmModal.requestCount}</strong> time-off request{deleteConfirmModal.requestCount !== 1 ? 's' : ''}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                        {/* Footer */}
                        <div style={{ padding: '12px 24px 20px', display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                            <button className="btn btn-outline-secondary" style={{ fontSize: 13, minWidth: 90 }}
                                onClick={() => setDeleteConfirmModal(null)}>
                                Cancel
                            </button>
                            <button className="btn btn-danger" style={{ fontSize: 13, minWidth: 120 }}
                                onClick={confirmDeletePolicy}>
                                Yes, Delete Policy
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <ConfirmModal
                show={!!confirmDeleteReq}
                title="Delete Time-Off Request?"
                message="This time-off request will be permanently removed."
                confirmText="Delete"
                onConfirm={() => { mutDeleteReq.mutate(confirmDeleteReq); setConfirmDeleteReq(null); }}
                onCancel={() => setConfirmDeleteReq(null)}
            />
        </div>
    );
}

function HolidayModal({ holiday, onClose, onSave }) {
    const [form, setForm] = useState({
        name: holiday?.name ?? '',
        date: holiday?.date ?? '',
        hours: holiday?.hours ?? 8,
        appliesTo: holiday?.appliesTo ?? 'salaried',
        isActive: holiday?.isActive ?? true,
    });
    const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

    return (
        <Modal show onHide={onClose} centered>
            <Modal.Header closeButton style={{ background: '#1B75F0', color: '#fff' }}>
                <Modal.Title style={{ fontSize: 16 }}>{holiday ? 'Edit Holiday' : 'Add Holiday'}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className="mb-3">
                    <label className="form-label" style={{ fontSize: 13, fontWeight: 600 }}>Holiday Name</label>
                    <input className="form-control form-control-sm" value={form.name} onChange={e => set('name', e.target.value)} placeholder="e.g. Memorial Day" />
                </div>
                <div className="mb-3">
                    <label className="form-label" style={{ fontSize: 13, fontWeight: 600 }}>Date</label>
                    <input type="date" className="form-control form-control-sm" value={form.date?.slice(0,10) ?? ''} onChange={e => set('date', e.target.value)} />
                </div>
                <div className="mb-3">
                    <label className="form-label" style={{ fontSize: 13, fontWeight: 600 }}>Hours</label>
                    <input type="number" className="form-control form-control-sm" value={form.hours} min="0" step="0.5" onChange={e => set('hours', parseFloat(e.target.value) || 8)} />
                </div>
                <div className="mb-3">
                    <label className="form-label" style={{ fontSize: 13, fontWeight: 600 }}>Applies To</label>
                    <select className="form-select form-select-sm" value={form.appliesTo} onChange={e => set('appliesTo', e.target.value)}>
                        <option value="salaried">Salaried employees only</option>
                        <option value="all">All employees (salaried + hourly)</option>
                    </select>
                </div>
                <div className="d-flex align-items-center gap-2">
                    <label className="form-label mb-0" style={{ fontSize: 13, fontWeight: 600 }}>Active</label>
                    <div onClick={() => set('isActive', !form.isActive)}
                        style={{ width: 40, height: 22, borderRadius: 11, background: form.isActive ? '#4CAF50' : '#ccc', cursor: 'pointer', position: 'relative', transition: 'background 0.2s' }}>
                        <div style={{ position: 'absolute', top: 3, left: form.isActive ? 20 : 3, width: 16, height: 16, borderRadius: '50%', background: '#fff', transition: 'left 0.2s' }} />
                    </div>
                </div>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" size="sm" onClick={onClose}>Cancel</Button>
                <Button size="sm" style={{ backgroundColor: '#1B75F0', border: 'none' }}
                    disabled={!form.name || !form.date}
                    onClick={() => onSave(form)}>
                    {holiday ? 'Save Changes' : 'Add Holiday'}
                </Button>
            </Modal.Footer>
        </Modal>
    );
}

export default TimeOff;
